import React, { useEffect, useState } from 'react';
import { reaction } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import {
    AccountTabs,
    AccountOverlay,
    TabsWrapper,
    EmptyTab,
} from 'src/domains/players/webview/components/Account/accountParts/Account.style';
import { Tab } from 'src/domains/players/webview/components/Account/accountParts/Tab';
import { AccountDrawerState } from 'src/domains/players/webview/components/Account/accountDrawer/AccountDrawer.state';
import { AccountTabsBuilderState } from 'src/domains/players/webview/components/Account/accountDrawer/AccountTabsBuilder.state';
import { RouteViewType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { Common, useCommon } from 'src/domains/common/Common';
import { CmsDrawerPagesState, PageType } from 'src/domains/players/state/CmsDrawerPagesState';
import { AppLayoutsState } from 'src/domains/layouts/state/AppLayouts.state';
import { SignUpState } from '../../SignUp/signupPrimary/signupState/SignUp.state';

export class AccountDrawerLocalState {
    public readonly tabBuilderData: AccountTabsBuilderState;
    public readonly accountDrawerData: AccountDrawerState;

    public constructor(common: Common) {
        this.tabBuilderData = AccountTabsBuilderState.get(common);

        this.accountDrawerData = new AccountDrawerState(this.getTabBuilderState(), common);
    }

    public getTabBuilderState = (): AccountTabsBuilderState => {
        return this.tabBuilderData;
    };

    public getAccountDrawerState = (): AccountDrawerState => {
        return this.accountDrawerData;
    };
}

interface AccountDrawerPropsType {
    currentView: RouteViewType | null;
}

const isOverlayFullCover = (
    starRouter: StarRouter,
    newGamblingCommissionRegulations: boolean,
    pageType: PageType | null
): boolean => {
    if (newGamblingCommissionRegulations === true) {
        const staticRouteName = starRouter.staticParam.param ?? '';

        return (
            ['safer-gambling', 'responsible-gambling'].includes(staticRouteName) ||
            ['safer-gambling', 'responsible-gambling'].includes(pageType ?? '')
        );
    }

    return false;
};

export const AccountDrawer = observer('AccountDrawer', (props: AccountDrawerPropsType): JSX.Element | null => {
    const common = useCommon();
    const appLayoutsState = AppLayoutsState.get(common);
    const { bannersBoxState, popupState } = appLayoutsState;
    const configComponents = ConfigComponents.get(common);
    const starRouter = StarRouter.get(common);
    const { config } = configComponents;
    const cmsDrawerPagesState = CmsDrawerPagesState.get(common);
    const signUpState = SignUpState.get(common);

    const routeName = props.currentView?.name ?? '';
    const overlayFullCover = isOverlayFullCover(
        starRouter,
        config.isSaferGambling.available,
        cmsDrawerPagesState.pageType
    );

    const [drawerState] = useState(() => new AccountDrawerLocalState(common));

    const state = drawerState.getAccountDrawerState();

    useEffect((): (() => void) => {
        state.setIsMounted();
        return reaction(
            () => state.futureTabName,
            () => state.onNewTabRequest()
        );
    }, [routeName]);

    /**
     * Some sub-components of this Component trigger `redirect` immediately after mounting (see `VerifyAccountTab`).
     * Mentioned `redirect` had been done before this component were mounted.
     * Because this component is listening to "redirects" (AFTER it is mounted - because of `useEffect`), we have to do it in that way.
     * For now, `AccountDrawer` is mounted before its sub-components.
     */

    if (state.isMounted === false) {
        return null;
    }

    const { currentTabData, futureTabData, currentTabDataForHeader, futureTabDataForHeader } = state;
    let currentTab = <EmptyTab key='empty-tab' />;
    if (state.currentTabName !== null) {
        currentTab = (
            <Tab
                key={state.currentTabName.account}
                tabName={state.currentTabName}
                showHeader={state.shouldDisplayMainHeader(state.currentTabName)}
                tabData={currentTabData}
                tabDataForHeader={currentTabDataForHeader}
            />
        );
    }

    let futureTab = null;
    if (state.areTabsDiffer && state.futureTabName !== null) {
        futureTab = (
            <Tab
                key={state.futureTabName.account}
                tabName={state.futureTabName}
                showHeader={state.shouldDisplayMainHeader(state.futureTabName)}
                tabData={futureTabData}
                tabDataForHeader={futureTabDataForHeader}
            />
        );
    }

    const handleOverlayClick = (): void => {
        if (signUpState.currentView.isCloseOptionBlocked) {
            console.warn('Close is not allowed');
            return;
        }
        starRouter.closeAccount();
    };

    return (
        <>
            <AccountTabs
                desktopNoRightSpace={popupState.rightPanelAttachedToRightEdge}
                key='account__tabs--with-header'
                isInvisible={!state.isOpened}
                topOffset={bannersBoxState.boxHeight}
                data-test='Tab'
            >
                <TabsWrapper
                    className='account__tab'
                    isLeaving={state.isDuringForwardAnimation}
                    isEntering={state.isDuringBackwardAnimation}
                    animationDuration={state.animationDurationInSeconds}
                    onTransitionEnd={state.finishAnimation}
                    data-test={state.isDuringAnimation ? 'animating' : ''}
                >
                    {currentTab}
                    {futureTab}
                </TabsWrapper>
            </AccountTabs>

            <AccountOverlay
                isVisible={state.isOpening}
                onClick={handleOverlayClick}
                isFullCover={overlayFullCover}
            />
        </>
    );
});
